import { App as CapacitorApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { Capacitor } from "@capacitor/core";
import type { User, Session } from "@mash/types";
import { captureException } from "@sentry/react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { create } from "zustand";

import { markAcquisitionSource } from "../lib/acquisition";
import { resetAnalyticsIdentity } from "../lib/analyticsIdentity";
import { formatPasswordResetError } from "../lib/authErrors";
import {
  clearPasswordRecoveryPending,
  getPasswordResetRedirectUrl,
  isRecoveryAuthState,
  isRecoveryUrl,
  isResetPasswordAppShell,
  markPasswordRecoveryPending,
  notePasswordRecoveryFromUrl,
} from "../lib/authRecovery";
import { getAppUrl, setLoggedInHint } from "../lib/hostname";
import {
  clearPendingJoinCode,
  readPendingJoinCode,
  rememberPendingJoinCode,
} from "../lib/pendingJoinCode";
import { takePendingTemplate } from "../lib/pendingTemplate";
import { clearCachedPushToken } from "../lib/pushStorage";
import { syncTimezone, clearTimezoneCache } from "../lib/syncTimezone";
import { inviteLinkApi } from "../services/userApi";

let supabase!: SupabaseClient;

interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  error: string | null;
  /** The last profile-row read failed, so `user` carries no identity fields. */
  profileLoadFailed: boolean;
  recoverySessionActive: boolean;
  _listenerInitialized: boolean;
  _fetchingUser: boolean;
  isAdmin: () => boolean;

  // Actions
  initialize: (client: SupabaseClient) => Promise<void>;
  /** Re-read the profile row for the signed-in account. Resolves to `true` on success. */
  refreshProfile: () => Promise<boolean>;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName?: string,
  ) => Promise<void>;
  signInWithGoogle: (inviteCode?: string) => Promise<void>;
  signInWithApple: (inviteCode?: string) => Promise<void>;
  resetPassword: (email: string) => Promise<boolean>;
  signOut: () => Promise<void>;
  clearError: () => void;
  _updateSession: (
    session: {
      access_token: string;
      refresh_token: string;
      expires_at?: number;
    } | null,
  ) => void;
  _fetchAndSetUser: (
    userId: string,
    session: {
      access_token: string;
      refresh_token: string;
      expires_at?: number;
      user?: { id: string; email?: string; created_at: string };
    },
  ) => Promise<void>;
}

type AuthSessionTokens = {
  access_token: string;
  refresh_token: string;
  expires_at?: number;
};

type ProfileRow = {
  display_name?: string | null;
  avatar_url?: string | null;
  username?: string | null;
  invite_accepted?: boolean | null;
  onboarding_completed?: boolean | null;
  role?: User["role"] | null;
};

const PROFILE_COLUMNS =
  "display_name, avatar_url, username, invite_accepted, onboarding_completed, role";

/**
 * Read the signed-in account's profile row, or `null` if the read failed.
 *
 * The failure used to be discarded: `data` came back null, every identity field
 * landed as `undefined`, and the account stayed signed in looking brand new —
 * the profile screen greeted a real user as "Your Profile" behind a "U" avatar
 * with no way to recover short of signing in again (GOM-299). Report it so the
 * cause is visible in Sentry, and let callers offer a retry instead.
 */
async function readProfileRow(userId: string): Promise<ProfileRow | null> {
  const { data, error } = await supabase
    .from("profiles")
    .select(PROFILE_COLUMNS)
    .eq("id", userId)
    .single();

  if (error) {
    console.error("[Auth] Profile read failed:", error.message);
    captureException(new Error(`Profile read failed: ${error.message}`), {
      tags: { area: "auth" },
      extra: {
        userId,
        code: error.code,
        details: error.details,
        hint: error.hint,
      },
    });
    return null;
  }

  return data as ProfileRow;
}

/**
 * Identity fields for the auth user. A failed read falls back to the previous
 * snapshot — a transient failure while re-authenticating must not wipe an
 * identity we already loaded — and leaves the access flags permissive, since
 * assuming `false` would lock the account out or replay onboarding.
 */
function profileFields(profile: ProfileRow | null, previous: User | null) {
  const source: ProfileRow = profile ?? {
    display_name: previous?.displayName,
    avatar_url: previous?.avatarUrl,
    username: previous?.username,
    invite_accepted: previous?.inviteAccepted,
    onboarding_completed: previous?.onboardingCompleted,
    role: previous?.role,
  };

  return {
    displayName: source.display_name ?? undefined,
    username: source.username,
    avatarUrl: source.avatar_url ?? undefined,
    role: source.role ?? "user",
    inviteAccepted: source.invite_accepted ?? true,
    onboardingCompleted: source.onboarding_completed ?? true,
  };
}

function activateRecoverySession(
  get: () => AuthState,
  set: (partial: Partial<AuthState>) => void,
  session: AuthSessionTokens,
) {
  get()._updateSession(session);
  set({ recoverySessionActive: true, user: null, loading: false });
}

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  session: null,
  loading: true,
  error: null,
  profileLoadFailed: false,
  recoverySessionActive: false,
  _listenerInitialized: false,
  _fetchingUser: false,

  // Admin status is the DB role (profiles.role), fetched in _fetchAndSetUser /
  // signIn — same source of truth as the backend and admin dashboard. Grant it
  // by setting profiles.role = 'admin' (locally: `pnpm seed:local`).
  isAdmin: () => get().user?.role === "admin",

  // Internal: update session tokens without refetching user
  _updateSession: session => {
    if (session) {
      const expiresAt = session.expires_at || 0;
      const now = Math.floor(Date.now() / 1000);
      const timeToExpiry = expiresAt - now;

      set({
        session: {
          accessToken: session.access_token,
          refreshToken: session.refresh_token,
          expiresAt: expiresAt,
        },
      });
      console.log("[Auth] Session tokens updated:", {
        expiresAt: new Date(expiresAt * 1000).toISOString(),
        timeToExpiry: `${Math.floor(timeToExpiry / 60)} minutes`,
      });
    } else {
      set({ user: null, session: null });
      console.log("[Auth] Session cleared");
    }
  },

  // Internal: fetch user profile and set full auth state.
  // Handles pending invite acceptance before reading the profile so
  // invite_accepted is already true by the time we set user state.
  // Uses _fetchingUser mutex to prevent concurrent calls from racing.
  _fetchAndSetUser: async (userId, session) => {
    if (get()._fetchingUser) return; // Another fetch is already running
    if (get().recoverySessionActive || isRecoveryUrl()) {
      activateRecoverySession(get, set, session);
      return;
    }
    set({ _fetchingUser: true });

    try {
      // Ensure session tokens are set so API calls have auth
      get()._updateSession(session);

      // Accept the pending invite BEFORE reading the profile. It has to be
      // awaited: acceptance is what flips `invite_accepted`, and a profile read
      // that overtakes it sets `inviteAccepted: false` on a user who just
      // earned access — which strands them on InviteLinkPrompt. That race is
      // the whole point of the ordering here, so don't make this fire-and-
      // forget again. A failure is non-fatal (already used, expired, quota) —
      // the profile read below then reports the real state.
      //
      // The code is read *without* being consumed, and cleared only once the
      // backend confirms. Clearing up front meant a failed accept discarded the
      // invite for good and the user was asked for their code all over again;
      // leaving it in place lets the next auth pass retry, bounded by the TTL
      // in `lib/pendingJoinCode.ts`.
      const pendingJoinCode = readPendingJoinCode();
      if (pendingJoinCode) {
        console.log("[Auth] Accepting pending invite:", pendingJoinCode);
        try {
          await inviteLinkApi.accept(pendingJoinCode);
          clearPendingJoinCode();
          console.log("[Auth] Pending invite accepted successfully");
        } catch (err: any) {
          console.warn("[Auth] Invite accept result:", err?.message);
        }
      }

      // A guest hit "Get app" on a share link (/t/:templateId) and went
      // through login/signup. Fires unconditionally on auth success, same
      // rationale as pendingGuestAppId above: per-screen `if (user)` redirects
      // aren't reliably reached on every auth path (OAuth full-page reloads,
      // the invite gate).
      // TTL'd: `?getTemplate=1` forks *and publishes*, so a stale intent would
      // silently create an app during an unrelated login days later.
      const pendingTemplateId = takePendingTemplate();
      if (pendingTemplateId) {
        setTimeout(() => {
          window.location.href = `/t/${pendingTemplateId}?getTemplate=1`;
        }, 100);
      }

      // Now fetch profile — invite_accepted is up-to-date
      const profile = await readProfileRow(userId);

      console.log(
        "[Auth] _fetchAndSetUser() setting user — invite_accepted from DB:",
        profile?.invite_accepted,
        "current store:",
        get().user?.inviteAccepted,
      );
      const expiresAt = session.expires_at || 0;
      const now = Math.floor(Date.now() / 1000);
      const timeToExpiry = expiresAt - now;
      const previous = get().user;

      set({
        user: {
          id: userId,
          email: session.user?.email || "",
          createdAt: session.user?.created_at || "",
          ...profileFields(profile, previous?.id === userId ? previous : null),
        },
        session: {
          accessToken: session.access_token,
          refreshToken: session.refresh_token,
          expiresAt: expiresAt,
        },
        loading: false,
        profileLoadFailed: !profile,
      });
      console.log("[Auth] User authenticated:", {
        email: session.user?.email,
        inviteAccepted: profile?.invite_accepted,
        inviteAcceptedFinal: profile?.invite_accepted ?? true,
        hadPendingJoinCode: !!pendingJoinCode,
        expiresAt: new Date(expiresAt * 1000).toISOString(),
        timeToExpiry: `${Math.floor(timeToExpiry / 3600)} hours`,
      });

      // Set cross-subdomain cookie so landing page can redirect to app
      setLoggedInHint(true);

      // Sync browser timezone to backend (fire-and-forget, cached in localStorage)
      syncTimezone();
    } catch (error) {
      console.error("[Auth] Failed to fetch user:", error);
      set({ user: null, session: null, loading: false });
    } finally {
      set({ _fetchingUser: false });
    }
  },

  initialize: async (client: SupabaseClient) => {
    supabase = client;
    const state = get();

    // Set up auth state change listener ONCE
    if (!state._listenerInitialized) {
      set({ _listenerInitialized: true });

      supabase.auth.onAuthStateChange(async (event, session) => {
        console.log("[Auth] Auth state change:", event);

        switch (event) {
          case "SIGNED_OUT":
            clearPasswordRecoveryPending();
            set({
              user: null,
              session: null,
              loading: false,
              profileLoadFailed: false,
              recoverySessionActive: false,
            });
            break;

          case "PASSWORD_RECOVERY":
            markPasswordRecoveryPending();
            if (session) {
              activateRecoverySession(get, set, session);
            } else {
              set({ recoverySessionActive: true, user: null, loading: false });
            }
            break;

          case "SIGNED_IN":
            // Invite accept + redirect logic is handled inside _fetchAndSetUser
            // (which also serializes concurrent calls via _fetchingUser mutex)
            if (session?.user) {
              const pathname =
                typeof window !== "undefined" ? window.location.pathname : "";
              if (
                !isResetPasswordAppShell(pathname, get().recoverySessionActive)
              ) {
                clearPasswordRecoveryPending();
                set({ recoverySessionActive: false });
              }
              get()._fetchAndSetUser(session.user.id, session);
            }
            break;

          case "INITIAL_SESSION":
            if (session?.user) {
              get()._fetchAndSetUser(session.user.id, session);
            } else {
              set({ user: null, session: null, loading: false });
            }
            break;

          case "TOKEN_REFRESHED":
            // Critical: Update stored tokens when Supabase refreshes them
            if (session) {
              get()._updateSession(session);
            }
            break;

          case "USER_UPDATED":
            // Refresh user data — skip during password recovery reset flow
            if (session?.user && !get().recoverySessionActive) {
              get()._fetchAndSetUser(session.user.id, session);
            }
            break;
        }
      });

      // Set up app lifecycle listeners for session refresh on resume
      // This prevents logout after inactivity by refreshing expired JWTs

      // Native apps: Listen for app resume
      if (Capacitor.isNativePlatform()) {
        CapacitorApp.addListener("appStateChange", async ({ isActive }) => {
          if (isActive) {
            console.log("[Auth] App resumed, checking session...");
            try {
              const {
                data: { session },
              } = await supabase.auth.getSession();
              if (session?.user) {
                console.log("[Auth] Session valid on resume");
                // Force a refresh if close to expiry (within 5 minutes)
                const expiresAt = session.expires_at || 0;
                const now = Math.floor(Date.now() / 1000);
                const timeToExpiry = expiresAt - now;
                if (timeToExpiry < 300) {
                  console.log("[Auth] Token close to expiry, refreshing...");
                  await supabase.auth.refreshSession();
                }
              } else {
                console.log("[Auth] No session on resume");
              }
            } catch (error) {
              console.error("[Auth] Session check on resume failed:", error);
            }
          }
        });
        console.log("[Auth] Native app lifecycle listener registered");

        // Listen for deep link (OAuth callback) on native
        CapacitorApp.addListener("appUrlOpen", async ({ url }) => {
          console.log("[Auth] App URL opened:", url);

          // Handle OAuth callback: app.forgeapps.main://oauth-callback#access_token=...
          if (url.startsWith("app.forgeapps.main://oauth-callback")) {
            // Close the system browser
            Browser.close().catch(() => {});

            // Extract tokens from the URL fragment
            const hashPart = url.split("#")[1];
            if (hashPart) {
              const params = new URLSearchParams(hashPart);
              const accessToken = params.get("access_token");
              const refreshToken = params.get("refresh_token");

              if (accessToken && refreshToken) {
                console.log("[Auth] Setting session from OAuth callback");
                const { error } = await supabase.auth.setSession({
                  access_token: accessToken,
                  refresh_token: refreshToken,
                });
                if (error) {
                  console.error(
                    "[Auth] Failed to set session from OAuth:",
                    error,
                  );
                  set({ error: error.message, loading: false });
                }
              }
            }
          }
        });
        console.log("[Auth] Native deep link listener registered");
      }

      // Web: Listen for visibility change
      if (!Capacitor.isNativePlatform()) {
        document.addEventListener("visibilitychange", async () => {
          if (!document.hidden) {
            console.log("[Auth] Page visible, checking session...");
            try {
              const {
                data: { session },
              } = await supabase.auth.getSession();
              if (session?.user) {
                console.log("[Auth] Session valid on visibility change");
                // Force a refresh if close to expiry (within 5 minutes)
                const expiresAt = session.expires_at || 0;
                const now = Math.floor(Date.now() / 1000);
                const timeToExpiry = expiresAt - now;
                if (timeToExpiry < 300) {
                  console.log("[Auth] Token close to expiry, refreshing...");
                  await supabase.auth.refreshSession();
                }
              } else {
                console.log("[Auth] No session on visibility change");
              }
            } catch (error) {
              console.error(
                "[Auth] Session check on visibility change failed:",
                error,
              );
            }
          }
        });
        console.log("[Auth] Web visibility listener registered");
      }

      console.log("[Auth] Auth state listener initialized");
    }

    notePasswordRecoveryFromUrl();

    // Get current session
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (session?.user) {
        if (isRecoveryAuthState(get().recoverySessionActive)) {
          activateRecoverySession(get, set, session);
        } else {
          await get()._fetchAndSetUser(session.user.id, session);
        }
      } else {
        set({ user: null, session: null, loading: false });
      }
    } catch (error) {
      console.error("[Auth] Initialization failed:", error);
      set({ user: null, session: null, loading: false });
    }
  },

  // The identity snapshot is taken once, at sign-in. Re-read it so a failed read
  // is recoverable in place — screens that show the account's identity expose
  // this as a retry instead of rendering a placeholder profile (GOM-299).
  refreshProfile: async () => {
    const { user } = get();
    if (!supabase || !user) return false;

    const profile = await readProfileRow(user.id);
    set({
      user: {
        id: user.id,
        email: user.email,
        createdAt: user.createdAt,
        ...profileFields(profile, user),
      },
      profileLoadFailed: !profile,
    });

    return !!profile;
  },

  signIn: async (email: string, password: string) => {
    set({ loading: true, error: null });

    try {
      console.log("[Auth] Signing in with email:", email);
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      console.log("[Auth] Sign in response:", data, error);

      if (error) {
        set({ error: error.message, loading: false });
        return;
      }

      if (data.user && data.session) {
        const profile = await readProfileRow(data.user.id);

        console.log(
          "[Auth] signIn() setting user — invite_accepted from DB:",
          profile?.invite_accepted,
        );
        clearPasswordRecoveryPending();
        set({
          user: {
            id: data.user.id,
            email: data.user.email || "",
            createdAt: data.user.created_at,
            ...profileFields(profile, null),
          },
          session: {
            accessToken: data.session.access_token,
            refreshToken: data.session.refresh_token,
            expiresAt: data.session.expires_at || 0,
          },
          loading: false,
          profileLoadFailed: !profile,
          recoverySessionActive: false,
        });
      }
    } catch (error) {
      console.error("[Auth] Sign in error:", error);
      set({
        error: error instanceof Error ? error.message : "Sign in failed",
        loading: false,
      });
    }
  },

  signUp: async (email: string, password: string, displayName?: string) => {
    set({ loading: true, error: null });

    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { display_name: displayName } },
      });

      if (error) {
        set({ error: error.message, loading: false });
        return;
      }

      if (data.user) {
        // Create profile
        await supabase
          .from("profiles")
          .insert({
            id: data.user.id,
            display_name: displayName || email.split("@")[0],
          });

        // If email confirmation is disabled, sign in directly
        if (data.session) {
          set({
            user: {
              id: data.user.id,
              email: data.user.email || "",
              displayName: displayName,
              createdAt: data.user.created_at,
            },
            session: {
              accessToken: data.session.access_token,
              refreshToken: data.session.refresh_token,
              expiresAt: data.session.expires_at || 0,
            },
            loading: false,
          });
        } else {
          set({ loading: false });
        }
      }
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : "Sign up failed",
        loading: false,
      });
    }
  },

  signInWithGoogle: async (inviteCode?: string) => {
    set({ error: null });

    // Store join code so we can accept the invite after OAuth redirect
    if (inviteCode) {
      rememberPendingJoinCode(inviteCode);
    }
    markAcquisitionSource(inviteCode ? "Invite Link" : "Organic");

    const isNative = Capacitor.isNativePlatform();

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: isNative
          ? "app.forgeapps.main://oauth-callback"
          : getAppUrl(),
        skipBrowserRedirect: isNative,
        queryParams: { prompt: "select_account" },
      },
    });

    if (error) {
      console.error("[Auth] Google OAuth error:", error.message, error);
      clearPendingJoinCode();
      set({ error: error.message });
      return;
    }

    // On native, open the OAuth URL in the system browser
    if (isNative && data?.url) {
      await Browser.open({ url: data.url });
    }
  },

  signInWithApple: async (inviteCode?: string) => {
    set({ error: null });

    if (inviteCode) {
      rememberPendingJoinCode(inviteCode);
    }
    markAcquisitionSource(inviteCode ? "Invite Link" : "Organic");

    const isNative = Capacitor.isNativePlatform();

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: "apple",
      options: {
        redirectTo: isNative
          ? "app.forgeapps.main://oauth-callback"
          : getAppUrl(),
        skipBrowserRedirect: isNative,
      },
    });

    if (error) {
      console.error("[Auth] Apple OAuth error:", error.message, error);
      clearPendingJoinCode();
      set({ error: error.message });
      return;
    }

    if (isNative && data?.url) {
      await Browser.open({ url: data.url });
    }
  },

  resetPassword: async (email: string) => {
    set({ error: null });

    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: getPasswordResetRedirectUrl(),
    });

    if (error) {
      set({ error: formatPasswordResetError(error) });
      return false;
    }

    return true;
  },

  signOut: async () => {
    // Clear state immediately so the UI redirects to login without waiting
    set({
      user: null,
      session: null,
      loading: false,
      profileLoadFailed: false,
      recoverySessionActive: false,
    });
    clearTimezoneCache();
    setLoggedInHint(false);
    clearPasswordRecoveryPending();
    // The cached device token belongs to the user who registered it. Leaving it
    // behind would let the next account inherit this one's push subscription.
    clearCachedPushToken();
    // Same hazard, for analytics: without a reset the next account to sign in
    // on this device keeps the same Mixpanel distinct_id, merging two people
    // into one profile.
    resetAnalyticsIdentity();

    try {
      console.log("[Auth] Signing out local session only");
      await supabase.auth.signOut({ scope: "local" });
      console.log("[Auth] Local signout complete");
    } catch (error) {
      // Session already cleared above — just log the error
      console.error("[Auth] Signout error:", error);
    }
  },

  clearError: () => set({ error: null }),
}));
